import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';
import * as AdminViewModels from '../../../../app/viewmodels/AdminViewModels';
import { KeyDateKind, ResourceSelectorId } from '../../../extViewmodels';
import { IModalContext, ModalChildComponentContextKey } from '../../../models';
import { useDeleteKeyFactsMutation } from '../../../queries/KeyFacts/useDeleteKeyFacts';
import { Button } from '../../../web/components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { asModalComponent } from '../../../web/components/Modal';
import { WarningIcon } from '../../../web/components/svgs/icons/WarningIcon';
import { styleSheet } from './styles';

const DeleteKeyFactsConfirmationOptions: IConfirmationDialogOption[] = [
	{
		isDestructive: true,
		title: 'Delete',
	},
	{
		isCancel: true,
		title: 'Cancel',
	},
];

interface IProps extends IModalContext {
	userSession: AdminViewModels.AdminUserSessionContext;
}

const _DeleteKeyFactsModal = ({ parentModal: { onRequestClose } }: IProps) => {
	const [showConfirmation, setShowConfirmation] = React.useState<boolean>(false);

	const handleCancel = () => {
		onRequestClose();
	};

	const accountDetailsContext = React.useContext(AdminViewModels.AccountDetailsContext);

	const resourceSelectorSettings = accountDetailsContext.account?.preferences?.resourceSelectorSettings;
	const getCustomTitle = (kind: KeyDateKind) =>
		resourceSelectorSettings?.[kind as unknown as ResourceSelectorId]?.displayName || kind;

	const keyDateKinds = [
		{ value: KeyDateKind.Birthday, label: 'Birthday' },
		{ value: KeyDateKind.Renewal, label: 'Renewal' },
		{ value: KeyDateKind.Anniversary, label: 'Anniversary' },
		{ value: KeyDateKind.Wedding, label: 'Wedding' },
		{ value: KeyDateKind.FinancialReview, label: 'Client Review' },
		{ value: KeyDateKind.Custom1, label: getCustomTitle(KeyDateKind.Custom1) },
		{ value: KeyDateKind.Custom2, label: getCustomTitle(KeyDateKind.Custom2) },
		{ value: KeyDateKind.Custom3, label: getCustomTitle(KeyDateKind.Custom3) },
		{ value: KeyDateKind.Custom4, label: getCustomTitle(KeyDateKind.Custom4) },
		{ value: KeyDateKind.Custom5, label: getCustomTitle(KeyDateKind.Custom5) },
		{ value: KeyDateKind.Custom6, label: getCustomTitle(KeyDateKind.Custom6) },
		{ value: KeyDateKind.Custom7, label: getCustomTitle(KeyDateKind.Custom7) },
		{ value: KeyDateKind.Custom8, label: getCustomTitle(KeyDateKind.Custom8) },
		{ value: KeyDateKind.Custom9, label: getCustomTitle(KeyDateKind.Custom9) },
		{ value: KeyDateKind.Custom10, label: getCustomTitle(KeyDateKind.Custom10) },
	];

	const impersonationContext = { account: accountDetailsContext.account };
	const deleteKeyFactsMutation = useDeleteKeyFactsMutation({
		onSuccess: () => {
			onRequestClose();
			toast.success('KeyFacts deleted successfully');
		},
		impersonationContext,
	});

	const validationSchema = yup.object({
		keyDateKind: yup.string().required('Key date kind is required'),
	});

	const methods = useForm<{
		keyDateKind: string | undefined;
	}>({
		defaultValues: {
			keyDateKind: undefined,
		},
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
	});
	const formValues = methods.watch();

	const selectedTitle = keyDateKinds.find(x => x.value === formValues.keyDateKind)?.label;

	const handleDeleteClick = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setShowConfirmation(true);
	};

	const handleConfirmationClose = (result: IConfirmationDialogOption<boolean>) => {
		if (result?.isDestructive) {
			deleteKeyFactsMutation
				.mutateAsync({
					keyFactKind: formValues.keyDateKind as KeyDateKind,
				})
				.catch(() => {
					toast.error('Failed to delete key facts');
				});
		}
		setShowConfirmation(false);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={handleDeleteClick}>
					<div className={css(styleSheet.wrapper)}>
						<h3 className={css(styleSheet.heading)}>Delete Key Facts</h3>
						<p className={css(styleSheet.text)}>Select the kind of key fact to delete:</p>
						{deleteKeyFactsMutation.isPending ? (
							<LoadingSpinner type='small' />
						) : (
							<>
								<Controller
									name='keyDateKind'
									control={methods.control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Select value={fieldProps.value ?? ''} onValueChange={fieldProps.onChange}>
											<SelectTrigger styles={styleSheet.selectTrigger}>
												<SelectValue placeholder='Select the Kind of Key Date' />
											</SelectTrigger>
											<SelectContent>
												{keyDateKinds.map(option => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									)}
								/>
								<footer className={css(styleSheet.footer)}>
									<Button
										variant='destructive'
										styles={[styleSheet.continueButton]}
										disabled={!methods.formState.isValid || methods.formState.isSubmitting}
										type='submit'
									>
										Delete
									</Button>
									<Button variant='secondary' onClick={handleCancel} type='button'>
										Cancel
									</Button>
								</footer>
							</>
						)}
					</div>
				</form>
			</FormProvider>
			<ConfirmationDialog
				className={css(styleSheet.confirmationDialog)}
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showConfirmation,
					onRequestClose: handleConfirmationClose,
				}}
				options={DeleteKeyFactsConfirmationOptions}
				title={`Are you sure you want to delete all ${selectedTitle} key facts for this account? This operation cannot be undone.`}
			/>
		</>
	);
};

const DeleteKeyFactsModalAsObserver = observer(_DeleteKeyFactsModal);
const DeleteKeyFactsModalWithContext = inject(ModalChildComponentContextKey)(DeleteKeyFactsModalAsObserver);
export const DeleteKeyFactsModal = asModalComponent(DeleteKeyFactsModalWithContext, {
	className: 'schedule-meeting--modal',
	useDefaultHeader: true,
});
